import { Injectable } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { PageInfo } from '../models/page-info';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { BehaviorSubject } from 'rxjs/Rx';
import {isSkycash, noop, salesChannel} from '../../core/utils/utils';
import {SalesChannel} from '../../core/consts/sales-channel.enum';

@Injectable({
  providedIn: 'root'
})
export class IframeCommunicationService {

  pageInfoReadySubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private containerElement: HTMLElement;
  private parentPageInfo: PageInfo = null;
  private visibleContentBelowIframe: number;
  private isModalOpened = false;
  private firstPositiveOffsetTop: number = 0;

  private iframeConfig: IframeConfig = {
    hideParentTopOnModalOpen: true
  };

  constructor(private overlayContainer: OverlayContainer) {
    window['iFrameResizer'] = {
      onReady: () => this.init()
    };
    this.insertIframeResizerScript();
  }

  init() {
    if (window['parentIFrame']) {
      window['parentIFrame'].getPageInfo(pageInfo => {
        isNotNullOrUndefined(this.parentPageInfo) ? noop() : this.pageInfoReadySubject.next(true);
        this.parentPageInfo = pageInfo;
        if (this.firstPositiveOffsetTop === 0 && this.parentPageInfo.offsetTop > 0) {
          this.firstPositiveOffsetTop = this.parentPageInfo.offsetTop;
        }
        if (this.isModalOpened) {
          this.calculateVisibleContentBelowIframe();
          this.updateOverlayContainer();
        }
      });
    }
  }

  insertIframeResizerScript(): void {
    const script = document.createElement('script');
    script.src = 'assets/js/iframeResizer.contentWindow.min.js';
    script.type = 'application/javascript';
    document.head.appendChild(script);
  }

  onModalOpen() {
    this.forceResize();
    this.isModalOpened = true;
    if (window['parentIFrame']) {
      this.sendMessage(IframeMessage.DISABLE_SCROLLING);

      this.calculateVisibleContentBelowIframe();
      if (this.parentPageInfo) {
        if (this.visibleContentBelowIframe > 0) { // there is visible content below an iframe - scroll to end of the iframe
          let scrollY = this.parentPageInfo.offsetTop + this.parentPageInfo.iframeHeight - this.parentPageInfo.clientHeight;
          window['parentIFrame'].scrollTo(0, scrollY);
        }
        this.updateOverlayContainer();
        // fix dialog scroll position in Safari
        setTimeout(() => document.querySelector('.cdk-overlay-pane').scroll(0, 0), 1);
      }
    }
  }

  onModalClose() {
    this.isModalOpened = false;
    this.sendMessage(IframeMessage.ENABLE_SCROLLING);
  }

  onStepChange(): void {
    this.sendMessage(IframeMessage.STEP_CHANGE);
  }

  forceResize() {
    this.sendMessage(IframeMessage.FORCE_RESIZE);
  }

  private sendMessage(message: IframeMessage): void {
    if (window['parentIFrame']) {
      window['parentIFrame'].sendMessage(message);
    }
  }

  private updateOverlayContainer() {
    if (!this.containerElement) {
      this.containerElement = this.overlayContainer.getContainerElement();
    }

    const dialogHeight = this.calculateDialogHeight();
    const dialogTopOffset = isSkycash() ? 0 : this.calculateDialogTopOffset();

    if (this.iframeConfig.hideParentTopOnModalOpen && dialogTopOffset === 0) {
      window['parentIFrame'].scrollTo(0, this.parentPageInfo.offsetTop);
    }

    this.containerElement.style.height = `${dialogHeight}px`;
    this.containerElement.style.top = `${dialogTopOffset}px`;

    this.sendDebugData(dialogHeight, dialogTopOffset);
  }

  private sendDebugData(dialogHeight: number, dialogTopOffset: number) {
    if (window['parentIFrame']) {
      window['parentIFrame'].sendMessage({
        type: IframeMessage.DEBUG_DATA,
        data: {
          isSkyCash: isSkycash(),
          detectedToolbarHeight: this.firstPositiveOffsetTop,
          parentPageInfo: this.parentPageInfo,
          dialogHeight: dialogHeight,
          dialogTopOffset: dialogTopOffset
        }
      });
    }
  }

  /**
   * returns calculated height (in pixels) of overlay container
   */
  private calculateDialogHeight() {
    let dialogHeight = this.parentPageInfo.clientHeight;

    let visibleContentUponIframeHeight = this.parentPageInfo.offsetTop - this.parentPageInfo.scrollTop;
    if (visibleContentUponIframeHeight > 0) { // there is visible content above the iframe
      dialogHeight -= visibleContentUponIframeHeight;
    }

    if (isSkycash() && this.parentPageInfo.offsetTop < 0) {
      dialogHeight -= this.firstPositiveOffsetTop;
    }

    return dialogHeight;
  }

  /**
   * returns calculated top offset (in pixels) of overlay container
   */
  private calculateDialogTopOffset() {
    if (isNotNullOrUndefined(this.parentPageInfo)) {
      let dialogOffsetTop = 0;
      let visibleTopContentHeight = this.parentPageInfo.scrollTop - this.parentPageInfo.offsetTop - this.visibleContentBelowIframe;
      if (visibleTopContentHeight > 0) { // there is no visible content above the iframe
        dialogOffsetTop = visibleTopContentHeight;
      }
      return dialogOffsetTop;
    }
    return 0;
  }

  /**
   * sets current height of visible content below the iframe
   */
  private calculateVisibleContentBelowIframe() {
    if (isNotNullOrUndefined(this.parentPageInfo)) {
      let visibleContentBelowIframeHeight = this.parentPageInfo.scrollTop + this.parentPageInfo.clientHeight - (this.parentPageInfo.offsetTop + this.parentPageInfo.iframeHeight);
      if (visibleContentBelowIframeHeight > 0) {
        this.visibleContentBelowIframe = visibleContentBelowIframeHeight;
      } else {
        this.visibleContentBelowIframe = 0;
      }
    }
    return 0;
  }
}

export interface IframeConfig {
  hideParentTopOnModalOpen: boolean; // whether app should scroll parent page to iframe top when modal is opened
}

export enum IframeMessage {
  ENABLE_SCROLLING = 'enable-scrolling',
  DISABLE_SCROLLING = 'disable-scrolling',
  STEP_CHANGE = 'step-change',
  FORCE_RESIZE = 'force-resize',
  DEBUG_DATA = 'debug-data',
}
